/**
 * @module flow-list/components/FlowTagSections
 *
 * The grouped flows view: one collapsible section per tag (alphabetical),
 * with flows that carry multiple tags appearing under each of them, and a
 * neutral "Untagged" section pinned last. Section order is by tag name;
 * flow order within a section preserves the server sort (name / recently
 * run / recently created).
 *
 * Rendering is virtualized: the sections are flattened into a single row list
 * (a header row per section, plus a card row per flow only while the section is
 * expanded) and windowed with `@tanstack/react-virtual`, so orgs with thousands
 * of flows only ever mount the rows on screen. The whole page scrolls inside
 * AppShell's `[data-app-main]` container, which is the virtualizer's scroll
 * element — there is no nested scroll area.
 */
import React from 'react';
import { cn, TAG_COLORS, Text, type StatusTimelineEntry, type TagColor } from '@chatsdet/ui';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import { CaretRight } from '@phosphor-icons/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { GroupedWorkflowItem } from '../../../../hooks/useWorkflows';
import { FlowCard } from './FlowCard';
import type { FlowActionHandlers, LatestRunInfo } from './cells';
import type { OpenWorkflowIssueSummary } from '../types';

/** Section key reserved for flows with no tags. Never collides with tag ids (UUIDs). */
export const UNTAGGED_SECTION_KEY = 'untagged';

export type TagSection = {
  key: string;
  name: string;
  color: TagColor | null;
  flows: GroupedWorkflowItem[];
};

/** A flattened, virtualizable row: either a section header or one flow card. */
export type FlowRow =
  | { type: 'header'; section: TagSection }
  | { type: 'card'; section: TagSection; flow: GroupedWorkflowItem };

/** One grouping ref a flow carries (a tag, a feature, …). */
export type SectionRef = {
  id: string;
  name: string;
  color: TagColor | null;
};

/**
 * Group flows into alphabetical sections by an arbitrary ref accessor (tags,
 * features, …), with multi-ref flows appearing once per ref and a neutral
 * empty section pinned last (omitted when no flow lacks refs).
 * Pure — exported for unit testing.
 */
export function groupFlowsIntoSectionsBy(
  items: GroupedWorkflowItem[],
  getRefs: (flow: GroupedWorkflowItem) => SectionRef[],
  emptySection: { key: string; name: string }
): TagSection[] {
  const byRef = new Map<string, TagSection>();
  const empty: TagSection = {
    key: emptySection.key,
    name: emptySection.name,
    color: null,
    flows: [],
  };

  for (const flow of items) {
    const refs = getRefs(flow);
    if (refs.length === 0) {
      empty.flows.push(flow);
      continue;
    }
    for (const ref of refs) {
      let section = byRef.get(ref.id);
      if (!section) {
        section = {
          key: ref.id,
          name: ref.name,
          color: ref.color,
          flows: [],
        };
        byRef.set(ref.id, section);
      }
      section.flows.push(flow);
    }
  }

  const sorted = Array.from(byRef.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
  // The empty section is pinned last; sections emptied by filters don't exist
  if (empty.flows.length > 0) {
    sorted.push(empty);
  }
  return sorted;
}

/**
 * Group flows into alphabetical tag sections, with multi-tag flows appearing
 * once per tag and an "Untagged" section pinned last (omitted when empty).
 * Pure — exported for unit testing.
 */
export function groupFlowsIntoSections(items: GroupedWorkflowItem[]): TagSection[] {
  return groupFlowsIntoSectionsBy(
    items,
    (flow) =>
      flow.tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        color: (tag.color as TagColor) ?? 'gray',
      })),
    { key: UNTAGGED_SECTION_KEY, name: 'Untagged' }
  );
}

/**
 * Flatten sections into virtualizable rows: a header row per section, plus a
 * card row per flow only while the section is expanded. Collapsed sections
 * contribute only their header. Pure — exported for unit testing.
 */
export function buildFlowSectionRows(
  sections: TagSection[],
  isCollapsed: (sectionKey: string) => boolean
): FlowRow[] {
  const out: FlowRow[] = [];
  for (const section of sections) {
    out.push({ type: 'header', section });
    if (!isCollapsed(section.key)) {
      for (const flow of section.flows) {
        out.push({ type: 'card', section, flow });
      }
    }
  }
  return out;
}

export type FlowSectionsSharedProps = {
  /** True when the server's safety ceiling truncated the result set. */
  capped: boolean;
  workflowTimelineEntries: Record<string, StatusTimelineEntry[]>;
  diagnosedCategoryByWorkflow: Record<string, FailureDiagnosticCategory | null>;
  openIssuesByWorkflow: Record<string, OpenWorkflowIssueSummary[]>;
  latestRunByWorkflow: Record<string, LatestRunInfo | undefined>;
  isRunsFetching: boolean;
  membersByUserId: Map<string, { displayName: string | null; primaryEmail: string | null }>;
  actionHandlers: FlowActionHandlers;
  onToggleFavorite: (workflowId: string, next: boolean) => void;
  onOpen: (workflowId: string, modifiers?: { metaKey: boolean; ctrlKey: boolean }) => void;
  isSectionCollapsed: (sectionKey: string) => boolean;
  onSectionCollapsedChange: (sectionKey: string, collapsed: boolean) => void;
};

export type FlowTagSectionsProps = FlowSectionsSharedProps & {
  items: GroupedWorkflowItem[];
};

export type FlowSectionsViewProps = FlowSectionsSharedProps & {
  sections: TagSection[];
  /** Keys of sections whose hosting ref should be hidden from each card's chip list. */
  hideSectionRefOnCards?: boolean;
  /** Optional right-aligned actions on a section header (e.g. rename/delete menus). */
  renderHeaderActions?: (section: TagSection) => React.ReactNode;
  /** Optional per-card trailing actions (e.g. remove-from-section). */
  renderCardActions?: (section: TagSection, flow: GroupedWorkflowItem) => React.ReactNode;
};

export function FlowTagSections({ items, ...shared }: FlowTagSectionsProps) {
  const sections = React.useMemo<TagSection[]>(() => groupFlowsIntoSections(items), [items]);
  return <FlowSectionsView sections={sections} hideSectionRefOnCards {...shared} />;
}

export function FlowSectionsView({
  sections,
  capped,
  workflowTimelineEntries,
  diagnosedCategoryByWorkflow,
  openIssuesByWorkflow,
  latestRunByWorkflow,
  isRunsFetching,
  membersByUserId,
  actionHandlers,
  onToggleFavorite,
  onOpen,
  isSectionCollapsed,
  onSectionCollapsedChange,
  hideSectionRefOnCards = false,
  renderHeaderActions,
  renderCardActions,
}: FlowSectionsViewProps) {
  // Flatten to virtualizable rows. A collapsed section contributes only its
  // header; an expanded one also contributes a card row per flow (multi-tag
  // flows appear once per section, exactly as before). Recomputes when the
  // collapse predicate changes so toggling re-windows immediately.
  const collapsedKeys = sections
    .filter((s) => isSectionCollapsed(s.key))
    .map((s) => s.key)
    .join(',');
  const rows = React.useMemo<FlowRow[]>(
    () => buildFlowSectionRows(sections, isSectionCollapsed),
    // collapsedKeys captures the (string) collapse state; isSectionCollapsed is
    // stable per render and intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, collapsedKeys]
  );

  const listRef = React.useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    // The page scrolls inside AppShell's [data-app-main] container; the
    // virtualizer windows against that ancestor, so the whole page scrolls as
    // before (header + filter bar scroll away, no nested scroll area).
    getScrollElement: () => listRef.current?.closest('[data-app-main]') ?? null,
    estimateSize: (i) => (rows[i]?.type === 'header' ? 44 : 50),
    overscan: 8,
    getItemKey: (i) => {
      const row = rows[i];
      return row.type === 'header' ? `h:${row.section.key}` : `${row.section.key}:${row.flow.id}`;
    },
  });

  return (
    <div>
      {capped && (
        <Text size="sm" className="text-muted-foreground mb-2 block">
          Showing the first 2,000 flows — narrow your filters to see everything.
        </Text>
      )}
      <div ref={listRef} style={{ position: 'relative', height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {row.type === 'header' ? (
                <SectionHeaderRow
                  section={row.section}
                  collapsed={isSectionCollapsed(row.section.key)}
                  onToggle={() =>
                    onSectionCollapsedChange(row.section.key, !isSectionCollapsed(row.section.key))
                  }
                  actions={renderHeaderActions?.(row.section)}
                />
              ) : (
                <div className={cn('pb-1.5', renderCardActions && 'flex items-center gap-1.5')}>
                  <div className="flex-1 min-w-0">
                    <FlowCard
                      flow={row.flow}
                      sectionTagId={hideSectionRefOnCards ? row.section.key : null}
                      entries={workflowTimelineEntries[row.flow.id] ?? []}
                      diagnosedCategory={diagnosedCategoryByWorkflow[row.flow.id] ?? null}
                      openIssues={openIssuesByWorkflow[row.flow.id] ?? []}
                      latestRun={latestRunByWorkflow[row.flow.id]}
                      isRunsFetching={isRunsFetching}
                      member={row.flow.createdBy ? membersByUserId.get(row.flow.createdBy) : null}
                      actionHandlers={actionHandlers}
                      onToggleFavorite={onToggleFavorite}
                      onOpen={onOpen}
                    />
                  </div>
                  {renderCardActions?.(row.section, row.flow)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Section header row — the flattened, non-animated equivalent of the previous
 * `CollapsibleSection` header (caret + colored dot + name + count badge).
 */
function SectionHeaderRow({
  section,
  collapsed,
  onToggle,
  actions,
}: {
  section: TagSection;
  collapsed: boolean;
  onToggle: () => void;
  /** Right-aligned extras (menus) — rendered OUTSIDE the toggle button. */
  actions?: React.ReactNode;
}) {
  const dotClass = section.color
    ? (TAG_COLORS[section.color] ?? TAG_COLORS.gray).dot
    : 'bg-muted-foreground/40';
  return (
    <div className="group/section flex items-center gap-2 w-full hover:bg-muted/30 transition-colors rounded-sm -mx-1 px-1 border-t border-border/50">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex items-center gap-2 flex-1 min-w-0 py-2.5 text-left"
      >
        <CaretRight
          size={14}
          weight="bold"
          className={cn(
            'text-muted-foreground transition-transform duration-200 shrink-0',
            !collapsed && 'rotate-90'
          )}
        />
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotClass}`} />
        <Text size="sm" weight="medium" className="truncate">
          {section.name}
        </Text>
        <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
          {section.flows.length}
        </span>
      </button>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
